import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Globe, ArrowRight, Loader2 } from "lucide-react";
import { useLanguage } from "@/App";
import { translations } from "@/i18n";
import { Reveal, Eyebrow } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-white/15 bg-[#101836] px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#D4AF37]";

const Contact = () => {
  const { lang } = useLanguage();
  const t = translations[lang].contact;
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    inquiry_type: translations.en.contact.types[0],
    message: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, language: lang });
      toast.success(t.success, { data: { testId: "contact-success-toast" } });
      setForm({ name: "", company: "", email: "", phone: "", inquiry_type: t.types[0], message: "" });
    } catch {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#060A1A] py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <Eyebrow>{t.eyebrow}</Eyebrow>
            <h2 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl">
              {t.title}
            </h2>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-slate-300 md:text-lg">
              {t.sub}
            </p>

            <div className="mt-12 space-y-6">
              <a
                data-testid="contact-email-link"
                href="mailto:hello@koamnv.com"
                className="group flex items-center gap-4 text-white/80 transition-colors duration-300 hover:text-[#D4AF37]"
              >
                <span className="flex h-11 w-11 items-center justify-center border border-white/15 transition-colors duration-300 group-hover:border-[#D4AF37]">
                  <Mail className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.emailLabel}
                  </span>
                  <span className="text-sm font-medium">hello@koamnv.com</span>
                </span>
              </a>
              <div className="flex items-center gap-4 text-white/80">
                <span className="flex h-11 w-11 items-center justify-center border border-white/15">
                  <MapPin className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.locationLabel}
                  </span>
                  <span className="text-sm font-medium">{t.location}</span>
                </span>
              </div>
              <div className="flex items-center gap-4 text-white/80">
                <span className="flex h-11 w-11 items-center justify-center border border-white/15">
                  <Globe className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-[10px] uppercase tracking-[0.3em] text-white/40">
                    {t.domainLabel}
                  </span>
                  <span className="text-sm font-medium">KOAMNV.com</span>
                </span>
              </div>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={0.1}>
            <form
              data-testid="consultation-form"
              onSubmit={submit}
              className="border border-white/10 bg-[#0A1128] p-8 lg:p-12"
            >
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-white/50" htmlFor="lead-name">
                    {t.name} *
                  </label>
                  <input
                    id="lead-name"
                    data-testid="lead-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder={t.namePh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-white/50" htmlFor="lead-company">
                    {t.company}
                  </label>
                  <input
                    id="lead-company"
                    data-testid="lead-company-input"
                    value={form.company}
                    onChange={set("company")}
                    placeholder={t.companyPh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-white/50" htmlFor="lead-email">
                    {t.email} *
                  </label>
                  <input
                    id="lead-email"
                    data-testid="lead-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder={t.emailPh}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-white/50" htmlFor="lead-phone">
                    {t.phone}
                  </label>
                  <input
                    id="lead-phone"
                    data-testid="lead-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder={t.phonePh}
                    className={inputCls}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-white/50" htmlFor="lead-type">
                    {t.type}
                  </label>
                  <select
                    id="lead-type"
                    data-testid="lead-type-select"
                    value={form.inquiry_type}
                    onChange={set("inquiry_type")}
                    className={`${inputCls} appearance-none`}
                  >
                    {t.types.map((ty) => (
                      <option key={ty} value={ty} className="bg-[#101836]">
                        {ty}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-white/50" htmlFor="lead-message">
                    {t.message} *
                  </label>
                  <textarea
                    id="lead-message"
                    data-testid="lead-message-input"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={t.messagePh}
                    className={`${inputCls} resize-none`}
                  />
                </div>
              </div>

              <button
                data-testid="lead-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-8 flex w-full items-center justify-center gap-3 bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-widest text-[#0A1128] transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? (
                  <>
                    {t.sending}
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    {t.submit}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
